import React, { useState, useEffect } from 'react';

import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import * as Api from '../../../api/api';
import { UiButton } from '../../../ui/ui-button';
import { UiDialog } from '../../../ui/ui-dialog';

import Message from '../../../widgets/legacy-message';
import { ReactComponent as LoadingLogo } from '../../../widgets/loading-logo.svg';
import {
  TableHeaderCell,
  HighlightedTableCell,
} from '../../../widgets/table-components/table-header-cell';
import { EmployeeDirectoryImportFileParser } from '../util/import-file-parser';

type Props = EmployeeDirectoryWizardStepProps;

export const EmployeeDirectoryImportDataMatching = ({
  employeeDirectoryImportId,
  onNext,
  onCancel,
  onPrevious,
  previousStepData,
  PageIndicator,
}: Props) => {
  const [parsedWorksheet, setParsedWorksheet] = useState<Array<{
    [key: string]: string | number | null;
  }> | null>(null);
  const [headers, setHeaders] = useState<Array<string> | null>(null);

  const [columnMappings, setColumnMappings] = useState<{ [csvColumn: string]: string }>({});
  const [usedFields, setUsedFields] = useState<{ [fieldName: string]: boolean }>({});

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);

  const { selectedHeaderRow, noHeaderRow, selectedWorksheet } = previousStepData;

  useEffect(() => {
    if (!hasInitialized) {
      Api.get(
        `/employee-directory-import/${employeeDirectoryImportId}/parse-file?headerRow=${
          selectedHeaderRow ?? '0'
        }${noHeaderRow ? '&noHeaderRow=true' : ''}${
          selectedWorksheet ? `&worksheetName=${selectedWorksheet}` : ''
        }`,
      )
        .then((resp) => {
          resp.json().then((data: any) => {
            if (data.errorMessage || typeof data !== 'object') {
              setErrorMessage('Failed to load data');
            } else {
              if (!noHeaderRow) {
                const { columnMappingsInitialMappings, initialUsedFields } =
                  EmployeeDirectoryImportFileParser.getInitialHeaderMapping(data.headers);
                setColumnMappings(columnMappingsInitialMappings);
                setUsedFields(initialUsedFields);
              }

              setParsedWorksheet(data.parsedWorksheet);
              setHeaders(data.headers);
            }
          });
        })
        .catch(() => {
          setErrorMessage('Failed to load data');
        });
      setHasInitialized(true);
    }
  }, [
    hasInitialized,
    employeeDirectoryImportId,
    noHeaderRow,
    selectedHeaderRow,
    selectedWorksheet,
  ]);

  const handleOnNext = async () => {
    onNext({
      parsedWorksheet,
      columnMappings,
      headers,
      headerRow: selectedHeaderRow,
      noHeaderRow,
      selectedWorksheet,
    });
  };

  const handleMappingChange =
    (selectedHeader: string) => (event: React.ChangeEvent<{ value: unknown }>) => {
      if (!selectedHeader) {
        return;
      }

      const selectedFieldName = event.target.value as string;
      setColumnMappings({
        ...columnMappings,
        [selectedHeader]: selectedFieldName,
      });

      const originalMapping = columnMappings[selectedHeader];
      const newUsedFields = EmployeeDirectoryImportFileParser.getUpdatedMapping(
        selectedFieldName,
        usedFields,
        originalMapping,
      );
      if (newUsedFields) {
        setUsedFields(newUsedFields);
      }
    };

  const previewRows = parsedWorksheet?.slice(0, 5);
  const everyElementHasBeenMapped = Object.keys(columnMappings).length === headers?.length;

  return (
    <>
      <UiDialog.Content>
        <PageIndicator />
        <span className="typo-h5">Map Headers to Team Engine Data Elements</span>
        <p className="typo-subtitle">
          The headers on the left are the columns that were selected in the previous step. Please
          let us know which Team Engine data elements they map to
        </p>
        {errorMessage ? <Message variant="error">{errorMessage}</Message> : null}
        {(!parsedWorksheet || !headers) && !errorMessage ? (
          <div className="u-textAlign-center">
            <LoadingLogo />
          </div>
        ) : null}
        {previewRows && headers ? (
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableHeaderCell>Customer Data Element</TableHeaderCell>
                <TableHeaderCell>Team Engine Data Element</TableHeaderCell>
                <TableHeaderCell>Preview</TableHeaderCell>
              </TableRow>
              {headers.map((h, index) => (
                <TableRow>
                  <HighlightedTableCell>
                    {!noHeaderRow ? h : `Column ${index + 1}`}
                  </HighlightedTableCell>
                  <TableCell>
                    <Select
                      value={columnMappings[h] || ''}
                      onChange={handleMappingChange(h)}
                      displayEmpty
                      renderValue={(val: any) =>
                        !val
                          ? 'Select data element'
                          : EmployeeDirectoryImportFileParser.getFieldMap()[val].displayName
                      }
                    >
                      {EmployeeDirectoryImportFileParser.getFields().map((field) => (
                        <MenuItem
                          id={field.fieldName}
                          value={field.fieldName}
                          disabled={usedFields[field.fieldName]}
                        >
                          {field.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    {EmployeeDirectoryImportFileParser.keyParsesIntoMultipleFields(
                      h,
                      columnMappings,
                    ) ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {EmployeeDirectoryImportFileParser.getResultingFieldsForKey(
                              h,
                              columnMappings,
                            ).map((rf) => (
                              <TableHeaderCell>
                                {EmployeeDirectoryImportFileParser.getFieldMap()[rf].displayName}
                              </TableHeaderCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewRows.map((row) =>
                            EmployeeDirectoryImportFileParser.getParsedPreview(
                              row,
                              h,
                              columnMappings,
                            ),
                          )}
                        </TableBody>
                      </Table>
                    ) : (
                      previewRows.map((row) =>
                        EmployeeDirectoryImportFileParser.getParsedPreview(row, h, columnMappings),
                      )
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton variant="dismiss" onClick={onCancel}>
          Cancel
        </UiButton>
        <div>
          <UiButton variant="secondary" onClick={onPrevious}>
            Back
          </UiButton>
          <UiButton
            variant="primary"
            className="u-margin-left"
            disabled={Boolean(errorMessage) || !everyElementHasBeenMapped}
            onClick={handleOnNext}
          >
            Next
          </UiButton>
        </div>
      </UiDialog.Actions>
    </>
  );
};
